import React, { useEffect, useRef } from "react";
import { Timeline } from "antd";
import type { TimelineProps } from "antd";
import { LoadingOutlined, ToolFilled, LinkOutlined } from "@ant-design/icons";
import { AnimatedMarkdown } from "flowtoken";
import "flowtoken/dist/styles.css";
import { AIReasoningStreamProps, Tools } from "./types";
import "./ai-reasoning-stream.scss";
import { FormThemeProvider } from "../Forms/FormThemeProvider";
import Badge from "../Badge";
import { getToolDisplayName, getToolUrl } from "../../../constants/ai-tool-names";

// Map chunk types to colors
const getChunkColor = (type: string): string => {
  switch (type) {
    case "reasoning":
      return "#a78bfa"; // Light lavender
    case "output":
      return "#8b5cf6"; // Medium purple
    case "userInput":
      return "#7c3aed"; // Deeper purple
    default:
      return "#a78bfa";
  }
};

const constructToolsWidget = (tools: Array<string> | undefined) => {
  if (!tools || tools.length === 0) {
    return null;
  }

  return (
    <div className="finspace-tools-widget-container">
      {tools.map((tool, index) => {
        const displayName = getToolDisplayName(tool);
        const url = getToolUrl(displayName);
        
        const badgeContent = (
          <Badge
            key={`tool-${index}-${tool}`}
            variant="category"
            color="purple"
            size="small"
            className={`finspace-tools-widget ${url ? 'finspace-tools-widget--clickable' : ''}`}
          >
            {displayName}
            {url && <LinkOutlined style={{ marginLeft: '4px', fontSize: '10px' }} />}
          </Badge>
        );

        if (url) {
          return (
            <a
              key={`tool-link-${index}-${tool}`}
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="finspace-tools-widget-link"
              aria-label={`Open ${displayName} in new tab`}
            >
              {badgeContent}
            </a>
          );
        }

        return badgeContent;
      })}
    </div>
  );
};

const getTimelineDot = (tools: Array<string> | undefined) => {
  if (tools && tools.length > 0) {
    return <ToolFilled style={{ fontSize: "16px" }} />;
  }
  return undefined;
};

const AIReasoningStream: React.FC<AIReasoningStreamProps> = ({
  chunks,
  autoScroll = true,
  className = "",
  showThinkingAnimation = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chunkRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const highlightTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  // Clean up stale references when chunks change
  useEffect(() => {
    const currentChunkIds = new Set(chunks.map(c => c.id));
    chunkRefs.current.forEach((_, id) => {
      if (!currentChunkIds.has(id)) {
        chunkRefs.current.delete(id);
        // Also clear any pending timeouts for removed chunks
        const timeout = highlightTimeouts.current.get(id);
        if (timeout) {
          clearTimeout(timeout);
          highlightTimeouts.current.delete(id);
        }
      }
    });
  }, [chunks]);

  // Auto-scroll to the bottom when chunks change
  useEffect(() => {
    if (autoScroll && containerRef.current) {
      // Add extra offset to ensure bottom content (including padding and "Thinking..." text) is fully visible
      const scrollOffset = 50;

      // Check if scrollTo is available (real browser), otherwise use scrollTop (tests)
      if (typeof containerRef.current.scrollTo === "function") {
        containerRef.current.scrollTo({
          top: containerRef.current.scrollHeight + scrollOffset,
          behavior: "smooth",
        });
      } else {
        // Fallback for test environments that don't implement scrollTo
        containerRef.current.scrollTop =
          containerRef.current.scrollHeight + scrollOffset;
      }
    }
  }, [chunks, autoScroll]);

  // Listen for step click events and scroll to matching chunks
  useEffect(() => {
    const handleStepClick = (event: Event) => {
      const customEvent = event as CustomEvent<{ stepId: string }>;
      const { stepId } = customEvent.detail;

      // Find chunks that match this step ID
      const matchingChunks = chunks.filter(
        (chunk) => chunk.steps && chunk.steps.includes(stepId)
      );

      if (matchingChunks.length === 0) return;

      // Get the first matching chunk's element
      const firstChunkId = matchingChunks[0].id;
      const firstChunkElement = chunkRefs.current.get(firstChunkId);

      if (firstChunkElement && containerRef.current) {
        // Scroll to the element
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const elementTop = firstChunkElement.getBoundingClientRect().top;
        const scrollOffset = elementTop - containerTop - 20; // 20px padding from top

        containerRef.current.scrollBy({
          top: scrollOffset,
          behavior: "smooth",
        });

        // First, remove highlight from all chunks and clear their timeouts
        // This ensures animation restarts properly even with rapid clicking
        chunkRefs.current.forEach((element, chunkId) => {
          const existingTimeout = highlightTimeouts.current.get(chunkId);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
            highlightTimeouts.current.delete(chunkId);
          }
          element.classList.remove("chunk-highlighted");
        });

        // Force reflow to restart CSS animation
        // This is necessary for the animation to restart when rapidly switching steps
        if (matchingChunks.length > 0) {
          const firstElement = chunkRefs.current.get(matchingChunks[0].id);
          if (firstElement) {
            void firstElement.offsetHeight;
          }
        }

        // Apply highlight to matching chunks
        matchingChunks.forEach((chunk) => {
          const element = chunkRefs.current.get(chunk.id);
          if (element) {
            element.classList.add("chunk-highlighted");
            
            // Set timeout to remove highlight after animation completes
            const timeoutId = setTimeout(() => {
              element.classList.remove("chunk-highlighted");
              highlightTimeouts.current.delete(chunk.id);
            }, 3000);
            
            highlightTimeouts.current.set(chunk.id, timeoutId);
          }
        });
      }
    };

    window.addEventListener("scrollToStepReasoning", handleStepClick);
    return () => {
      window.removeEventListener("scrollToStepReasoning", handleStepClick);
    };
  }, [chunks]);

  // Create items array for Timeline
  const timelineItems = chunks.map((chunk, index) => ({
    color: getChunkColor(chunk.type),
    dot: getTimelineDot(chunk.tools),
    children: (
      <div 
        ref={(el) => {
          if (el) {
            chunkRefs.current.set(chunk.id, el);
          } else {
            chunkRefs.current.delete(chunk.id);
          }
        }}
        className="ai-reasoning-stream__content"
      >
        <AnimatedMarkdown
          content={chunk.content}
          animation="fadeIn"
          animationDuration="0.5s"
          animationTimingFunction="ease-in-out"
        />
        {constructToolsWidget(chunk.tools)}
      </div>
    ),
  }));

  return (
    <div ref={containerRef} className={`ai-reasoning-stream ${className}`}>
      {chunks.length === 0 && showThinkingAnimation ? (
        <div className="thinking-shimmer">
          <div className="shimmer-bar"></div>
          <div className="shimmer-bar"></div>
          <div className="shimmer-bar"></div>
        </div>
      ) : (
        <FormThemeProvider>
          <Timeline
            pending={
              chunks.length > 0 && showThinkingAnimation && "Thinking..."
            }
            pendingDot={<LoadingOutlined spin style={{ color: "#a78bfa" }} />}
            items={timelineItems}
          />
        </FormThemeProvider>
      )}
    </div>
  );
};

export default AIReasoningStream;
